// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import debounce from 'lodash/debounce';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {downloadCSV, fetchUsers, fetchUsersCount} from 'common/report/queries/query-api/users-client';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {MPProperties} from 'common/constants';
import {PropertyType, ReportName} from 'common/report/constants';
import {asPropertyItem} from 'common/widgets/users-table/util';
import {addToast, closeToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {CohortActionEvent, CohortDetail} from 'common/widgets/create-cohort-button';
import {getLocation} from 'common/util/url';
import {IronContextRegistry} from 'common/report/context-registry';
import {
  fetchAllPropertyDefinitionsForGroupProfiles,
  getAllPropertyDefinitionsForGroupProfiles,
} from 'common/report/stores/property-definitions';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';

import {TrackingEvent} from '../tracking';
import {formatProperty, isMatchingProperty, preferScdProperty} from './utils';
import {SidebarUserListJsxProps} from './types';
import {template} from './template';
import css from './index.inline.styl';

const USER_PROPERTIES: PropertySectionItem[] = [
  asPropertyItem(MPProperties.Name, PropertyType.String),
  asPropertyItem(MPProperties.Email, PropertyType.String),
];
const GROUP_PROPERTIES: PropertySectionItem[] = [asPropertyItem(MPProperties.Name, PropertyType.String)];

const USER_COLUMNS = [
  {value: MPProperties.NameWithFallbacks, resourceType: `user`},
  {value: MPProperties.Email, resourceType: `user`},
  {value: MPProperties.LastSeen, resourceType: `user`},
  {value: MPProperties.Avatar, resourceType: `user`},
  {value: MPProperties.City, resourceType: `user`},
];
const GROUP_COLUMNS = [
  {value: MPProperties.NameWithFallbacks, resourceType: `user`},
  {value: MPProperties.LastSeen, resourceType: `user`},
  {value: MPProperties.Avatar, resourceType: `user`},
  {value: MPProperties.City, resourceType: `user`},
];

export class SidebarUserList extends Component<
  SidebarUserListJsxProps['state'],
  SidebarUserListJsxProps['attrs'],
  unknown,
  unknown,
  IronContextRegistry,
  SidebarUserListJsxProps['params']
> {
  declare fetchUsersAbortController?: AbortController;
  declare fetchUsersCountAbortController?: AbortController;

  static get attrsSchema(): AttrsSchema<SidebarUserListJsxProps['attrs']> {
    return {
      'hide-cohort-functionality': `boolean`,
      'include-all-users': `boolean`,
      'data-group-name': `string`,
      'report-name': {
        type: `string`,
        enum: [ReportName.Insights, ReportName.Flows, ReportName.Funnels, ReportName.Retention],
      },
      color: `string`,
      segments: {
        type: `json`,
        default: [],
      },
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      contexts: [`currentProject` as const, `i18n` as const],
      params: {
        createSegmentLabel: Function,
      },
      defaultState: {
        totalUsers: 0,
        error: ``,
        cohort: new Cohort(),
        cohortDetails: [],
        search: ``,
        users: [],
        loading: true,
        loadingCount: true,
      },
    };
  }

  getCurrentProject(): CurrentProject | undefined {
    return this.getContext(`currentProject`).get().value;
  }

  set cohort(cohort: Cohort) {
    this.update({cohort});
    this.fetchUsers();
    this.fetchUsersCount();
  }

  set cohortDetails(cohortDetails: CohortDetail[]) {
    this.update({
      cohortDetails,
    });
  }

  getCompanyNameKey() {
    return dataGroupStore.value[this.state.cohort.dataGroupId]?.metadata?.company_key_name;
  }

  hasCompanyNameKeyConfigured() {
    return this.getCompanyNameKey();
  }

  getCompanyNameKeyProperty() {
    const companyNameKey = this.getCompanyNameKey();
    if (!companyNameKey) {
      return null;
    }
    return formatProperty(
      Object.values(getAllPropertyDefinitionsForGroupProfiles())
        .filter((property) => isMatchingProperty(property, companyNameKey, this.state.cohort.dataGroupId))
        .reduce(preferScdProperty, null),
    );
  }

  async getGroupColumns() {
    const groupColumns = GROUP_COLUMNS.map((col) => ({...col, dataGroupId: this.state.cohort.dataGroupId}));
    if (this.hasCompanyNameKeyConfigured()) {
      await fetchAllPropertyDefinitionsForGroupProfiles(dataGroupStore.value);
      const companyNameKeyColumn = this.getCompanyNameKeyProperty();

      return companyNameKeyColumn ? [...groupColumns, companyNameKeyColumn] : groupColumns;
    }
    return groupColumns;
  }

  fetchUsers = debounce(async ({search = ``}: {search?: string} = {}) => {
    this.update({
      loading: true,
      ...(search ? {loadingCount: true} : {}),
    });
    try {
      if (this.fetchUsersAbortController) {
        this.fetchUsersAbortController.abort();
      }
      this.fetchUsersAbortController = new AbortController();
      const users = await fetchUsers({
        cohort: this.state.cohort,
        includeAllUsers: this.attr(`include-all-users`),
        search,
        columns: this.state.cohort.dataGroupId ? await this.getGroupColumns() : USER_COLUMNS,
        abortSignal: this.fetchUsersAbortController.signal,
      });
      this.update({
        loading: false,
        users,
        // search is not supported in the `/stat` endpoint for now, we'll have to use the total
        // users returned from /engage endpoint
        ...(search ? {totalUsers: users.length, loadingCount: false} : {}),
      });
    } catch (err) {
      if (err.name === `AbortError`) {
        return;
      }
      this.update({
        error: err.message,
        loading: false,
        ...(search ? {loadingCount: false} : {}),
      });
    }
  }, 300);

  async fetchUsersCount({search = ``}: {search?: string} = {}) {
    if (this.fetchUsersCountAbortController) {
      this.fetchUsersCountAbortController.abort();
    }
    this.fetchUsersCountAbortController = new AbortController();
    // search does not work in the fetchUsersCount endpoint for now
    if (search) {
      return;
    }
    this.update({
      loadingCount: true,
    });
    try {
      const totalUsersCountResponse = await fetchUsersCount({
        cohort: this.state.cohort,
        includeAllUsers: this.attr(`include-all-users`),
        abortSignal: this.fetchUsersCountAbortController.signal,
      });
      const totalUsers = totalUsersCountResponse.results?.webhook;
      this.update({
        loadingCount: false,
        totalUsers,
      });
    } catch (err) {
      if (err.name === `AbortError`) {
        return;
      }
      this.update({
        loadingCount: false,
        error: err.message,
      });
    }
  }

  handleUserSelected = (ev: CustomEvent) => {
    this.dispatchEvent(
      new CustomEvent(`userSelected`, {
        detail: {
          distinctID: ev.detail.distinctID,
        },
        composed: true,
      }),
    );
  };

  handleDownloadCsv = async () => {
    const {t} = this.getContext(`i18n`);
    const toast = addToast({message: t(`view-users.export-toast`), timeout: null});
    try {
      const url = await downloadCSV({
        cohort: this.state.cohort,
        includeAllUsers: this.attr(`include-all-users`),
        search: this.state.search,
        columns: this.state.cohort.dataGroupId ? await this.getGroupColumns() : USER_COLUMNS,
        queryHref: getLocation().href,
      });
      window.location.assign(url);
      mixpanel.track(TrackingEvent.ExportCSV, {
        'Report name': this.attr(`report-name`),
        ...this.state.cohort.toTrackingData(),
      });
      closeToast(toast);
    } catch (err) {
      const message = err.statusCode === 403 ? t(`view-users.error-unauthorized`) : t(`view-users.error-toast`);
      addToast({
        message,
        type: ToastType.ERROR,
        timeout: null,
      });
    }
  };

  handleSearch = (ev) => {
    this.update({search: ev.target.value});
    this.fetchUsers({search: ev.target.value});
    this.fetchUsersCount({search: ev.target.value});
  };

  handleCohortEvent = (eventName: string) => (ev: CohortActionEvent) => {
    const cohortDetail = this.state.cohortDetails.find((cohort) => cohort.type === ev.detail.type);
    const trackingData = Object.assign(cohortDetail.cohort.toTrackingData(), {
      'Cohort: has attribution filter': cohortDetail.cohort.isAttributionCohort(),
    });
    mixpanel.track(eventName, {
      'Report name': this.attr(`report-name`),
      ...trackingData,
    });
  };

  getShowingProperties() {
    return this.state.cohort.dataGroupId ? GROUP_PROPERTIES : USER_PROPERTIES;
  }
}

export const SidebarUserListEl = defineAndMakeJsxFactory(`mp-sidebar-user-list`, SidebarUserList);
export const SidebarUserListReact = panel2react<SidebarUserListJsxProps>(`mp-sidebar-user-list`, SidebarUserList);
